import React, { useEffect, useState } from 'react'
import { BrowserRouter, Route, Routes } from 'react-router-dom'
import { Box, CircularProgress, Typography } from '@mui/material'
import AcceptInvite from './components/auth/AcceptInvite'
import ForgotPassword from './components/auth/ForgotPassword'
import Login from './components/auth/Login'
import Register from './components/auth/register.js/Register'
import Onboarding from './components/auth/Onboarding'
import ResetPassword from './components/auth/ResetPassword'
import Dashboard from './components/Dashboard'
import PostHogPageviewTracker from './components/utils/PostHogPageViewTracker'
import CreateOrganization from './components/auth/CreateOrganization'
import FrontRouteGuard from './components/guards/FrontRouteGuard'
import ProtectedRoute from './components/guards/ProtectedRoute'
import AdminRoute from './components/guards/AdminRoute'
import AdminApp from './components/admin/AdminApp'
import SubscriptionEnded from './components/auth/SubscriptionEnded'
import NotFound from './components/dashboard/NotFound'
import {
  setOrganizationUpdateListener,
  clearOrganizationUpdateListener,
  runSubscriptionGate,
  awaitCheckoutSuccessAndUnlock,
  isCheckoutUnlockInProgress,
} from './utils/organizationService'

function hasCheckoutSuccessOnLoad() {
  try {
    return new URLSearchParams(window.location.search).get('checkout') === 'success'
  } catch {
    return false
  }
}

const Main = () => {
  const [organization, setOrganization] = useState(
    localStorage.getItem("organization") ? JSON.parse(localStorage.getItem("organization")) : null
  );
  const [checkoutActivating, setCheckoutActivating] = useState(hasCheckoutSuccessOnLoad);

  useEffect(() => {
    setOrganizationUpdateListener(setOrganization);
    return () => clearOrganizationUpdateListener();
  }, []);

  useEffect(() => {
    if (!hasCheckoutSuccessOnLoad()) return undefined;
    let cancelled = false;
    setCheckoutActivating(true);
    (async () => {
      try {
        await awaitCheckoutSuccessAndUnlock();
      } finally {
        if (!cancelled) setCheckoutActivating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!organization) return;
    if (isCheckoutUnlockInProgress()) return;
    runSubscriptionGate(organization, { pathname: window.location.pathname });
  }, [organization]);

  return (
   <>
    <BrowserRouter>
      <PostHogPageviewTracker/>
      {checkoutActivating && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            bgcolor: 'rgba(248, 250, 252, 0.96)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
          }}
        >
          <CircularProgress size={36} />
          <Typography fontWeight={600} color="text.primary">
            Activating your subscription…
          </Typography>
          <Typography fontSize={14} color="text.secondary">
            This usually only takes a moment.
          </Typography>
        </Box>
      )}
      <FrontRouteGuard>
        <Routes>
          <Route path="/dashboard/*" element={<ProtectedRoute><Dashboard/></ProtectedRoute>} />
          <Route
            path="/admin/*"
            element={
              <ProtectedRoute>
                <AdminRoute>
                  <AdminApp />
                </AdminRoute>
              </ProtectedRoute>
            }
          />
          <Route path="/login" element={<Login/>} />
          <Route path="/accept-invite/:token" element={<AcceptInvite/>} />
          <Route path="/create-organization/" element={<CreateOrganization/>} />
          <Route path="/forgot-password" element={<ForgotPassword/>} />
          <Route path="/reset-password/:data" element={<ResetPassword/>} />
          <Route path="/register" element={<Register/>} />
          <Route path="/onboarding" element={<Onboarding/>} />
          <Route path="/" element={<Login/>} />
          <Route path='/subscription-ended' element={<SubscriptionEnded/>} />
          <Route path='/*' element={<NotFound/>} />
        </Routes>
      </FrontRouteGuard>
    </BrowserRouter>
   </>
  )
}

export default Main
